import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { Button, Field, Notice, Select } from "../components";
import { Link, useRouter } from "../router";
import { NeedBranch, useTenant } from "../shell";
import { SEX_LABEL } from "./patientLabels";
import { addDays, dayLabel, istTimeLabel, istToday, weekday } from "./time";

interface Doctor { id: string; displayName: string; branchIds: string[]; specialities: string[] }
interface Appt { id: string; start: string; timeLabel: string; dateLabel: string; status: string; visitType: string; practitionerId: string; practitionerName: string;
  overbooked: boolean; version: number; note: string | null; cancelReason: string | null; branchId: string;
  patient: { id: string; uhid: string; fullName: string; age: string; sex: string; mobile: string | null } | null }
interface Slot { start: string; available: number; capacity: number; booked: number; branchId: string; room: string | null }

export const STATUS_TEXT: Record<string, string> = { booked: "Booked", confirmed: "Confirmed", arrived: "Arrived", waiting: "Waiting", in_consultation: "With doctor",
  completed: "Completed", cancelled: "Cancelled", no_show: "No-show", rescheduled: "Rescheduled" };
const VISIT_TEXT: Record<string, string> = { new: "New", follow_up: "Follow-up", walk_in: "Walk-in", emergency: "Emergency", home_visit: "Home visit", teleconsult: "Teleconsult" };
// Arrival, calling and completion run through the queue (Check in issues a token); here only confirmation is set directly.
const NEXT: Record<string, { to: string; label: string }[]> = { booked: [{ to: "confirmed", label: "Confirm" }] };

function useDoctors() {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  useEffect(() => { api.authed("/api/v1/practitioners").then((r) => setDoctors(r.items)).catch(() => {}); }, []);
  return doctors;
}

export function Appointments() {
  const { can } = useTenant();
  const q = new URLSearchParams(location.search);
  const doctors = useDoctors();
  const [doctor, setDoctor] = useState(q.get("doctor") ?? "");
  const [date, setDate] = useState(q.get("date") ?? istToday());
  const [mode, setMode] = useState<"day" | "week" | "month">("day");
  const [items, setItems] = useState<Appt[] | null>(null);
  const [days, setDays] = useState<{ date: string; capacity: number; booked: number }[] | null>(null);
  const [msg, setMsg] = useState<{ tone: "error" | "success" | "info"; text: string } | null>(null);

  const range = useMemo(() => {
    if (mode === "week") { const start = addDays(date, -((weekday(date) + 6) % 7)); return [start, addDays(start, 6)] as const; }
    if (mode === "month") { const first = date.slice(0, 8) + "01"; const start = addDays(first, -((weekday(first) + 6) % 7)); return [start, addDays(start, 41)] as const; }
    return [date, date] as const;
  }, [mode, date]);

  // Messages persist across reloads of the same view (so confirmations and waiting-list names stay visible);
  // they clear when the user moves to another date, doctor or view.
  useEffect(() => { setMsg(null); }, [mode, date, doctor]);
  const load = useCallback(() => {
    if (mode === "day") api.authed(`/api/v1/appointments?date=${date}${doctor ? `&practitionerId=${doctor}` : ""}`).then((r) => setItems(r.items)).catch((e) => setMsg({ tone: "error", text: e.message }));
    else api.authed(`/api/v1/appointments/summary?from=${range[0]}&to=${range[1]}${doctor ? `&practitionerId=${doctor}` : ""}`).then((r) => setDays(r.days)).catch((e) => setMsg({ tone: "error", text: e.message }));
  }, [mode, date, doctor, range]);
  useEffect(load, [load]);

  return (
    <>
      <div className="page-head"><h1>Appointments</h1>
        {can("appointment:create") && <Link to={`/app/appointments/new${doctor ? `?doctor=${doctor}` : ""}`} className="btn btn-primary">Book appointment</Link>}</div>
      <div className="toolbar">
        <div className="tabs compact" role="tablist" aria-label="View">
          {(["day", "week", "month"] as const).map((m) => <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)}>{m[0]!.toUpperCase() + m.slice(1)}</button>)}
        </div>
        <div className="toolbar-controls">
          <button className="btn btn-quiet" aria-label="Previous" onClick={() => setDate(addDays(date, mode === "day" ? -1 : mode === "week" ? -7 : -30))}>‹</button>
          <label className="sr-only" htmlFor="appt-date">Date</label>
          <input id="appt-date" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          <button className="btn btn-quiet" aria-label="Next" onClick={() => setDate(addDays(date, mode === "day" ? 1 : mode === "week" ? 7 : 30))}>›</button>
          <button className="btn btn-quiet" onClick={() => setDate(istToday())}>Today</button>
          <label className="sr-only" htmlFor="appt-doc">Doctor</label>
          <select id="appt-doc" value={doctor} onChange={(e) => setDoctor(e.target.value)}>
            <option value="">All doctors</option>{doctors.map((d) => <option key={d.id} value={d.id}>{d.displayName}</option>)}
          </select>
        </div>
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {mode === "day" && <>
        <h2 className="day-title">{dayLabel(date)}</h2>
        {items && items.length === 0 && <p className="empty">No appointments on this day.</p>}
        {items && items.length > 0 && <DayTable items={items} onChanged={(text, tone = "success", updated) => {
          // Show the server's updated row immediately, then refresh the whole day in the background.
          if (updated) setItems((xs) => xs?.map((x) => (x.id === updated.id ? updated : x)) ?? null);
          setMsg({ tone, text }); load(); }} />}
      </>}
      {mode !== "day" && days && (
        <div className={`cal cal-${mode}`} role="grid" aria-label={mode === "week" ? "Week" : "Month"}>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="cal-head" role="columnheader">{d}</div>)}
          {days.map((x) => {
            const out = mode === "month" && x.date.slice(0, 7) !== date.slice(0, 7);
            return (
              <button key={x.date} role="gridcell" className={`cal-day${out ? " out" : ""}${x.date === istToday() ? " today" : ""}`} onClick={() => { setDate(x.date); setMode("day"); }}
                aria-label={`${dayLabel(x.date)}: ${x.booked} booked of ${x.capacity}`}>
                <span className="cal-date">{Number(x.date.slice(8))}</span>
                {x.capacity > 0 || x.booked > 0 ? <span className="cal-load"><strong>{x.booked}</strong> / {x.capacity}</span> : <span className="cal-none">No sessions</span>}
                {x.capacity > 0 && <span className="cal-bar" aria-hidden="true"><span style={{ width: `${Math.min(100, Math.round((x.booked / x.capacity) * 100))}%` }} /></span>}
              </button>);
          })}
        </div>)}
      {mode !== "day" && <p className="muted small">Booked / available places from the doctors' schedules. Select a day to see appointments.</p>}
    </>
  );
}

function DayTable({ items, onChanged }: { items: Appt[]; onChanged: (text: string, tone?: "success" | "info", updated?: Appt) => void }) {
  const { can } = useTenant();
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [rescheduling, setRescheduling] = useState<Appt | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function change(a: Appt, to: string, why?: string) {
    setError(null);
    try {
      const r = await api.authed(`/api/v1/appointments/${a.id}/status`, { body: { to, version: a.version, reason: why ?? null } });
      setCancelling(null); setReason("");
      const w = r.waitlist as { fullName: string; uhid: string; mobile: string | null }[];
      onChanged(w?.length ? `${STATUS_TEXT[to]}. Waiting for this doctor today: ${w.map((x) => `${x.fullName} (${x.uhid}${x.mobile ? `, ${x.mobile}` : ""})`).join("; ")}.` : `${a.patient?.fullName}: ${STATUS_TEXT[to]?.toLowerCase()}.`,
        w?.length ? "info" : "success", r.appointment as Appt);
    } catch (ex) { setError((ex as ApiError).message); }
  }
  const pastStart = (a: Appt) => new Date(a.start).getTime() < Date.now();
  async function checkIn(a: Appt) {
    setError(null);
    try {
      const r = await api.authed("/api/v1/queue/check-in", { body: { appointmentId: a.id } });
      const billNote = r.bill?.invoiceId ? " Bill opened." : r.bill?.problem === "no_fee_set" ? " No consultation fee is set for this doctor, so no bill was opened." : "";
      onChanged(`${a.patient?.fullName}: token ${r.token.number}.${billNote}`, r.bill?.problem ? "info" : "success", { ...a, status: "waiting", version: a.version + 2 });
    } catch (ex) { setError((ex as ApiError).message); }
  }
  return (
    <>
      {error && <Notice tone="error">{error}</Notice>}
      <table className="data appts">
        <thead><tr><th scope="col">Time</th><th scope="col">Patient</th><th scope="col">Doctor</th><th scope="col">Visit</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>{items.map((a) => (
          <tr key={a.id} className={["cancelled", "no_show", "rescheduled"].includes(a.status) ? "dim" : ""}>
            <td className="mono">{a.timeLabel}{a.overbooked && <span className="tag-ob" title="Overbooked">+</span>}</td>
            <td>{a.patient ? <><Link to={`/app/patients/${a.patient.id}`}>{a.patient.fullName}</Link><br /><span className="muted small">{a.patient.uhid} · {a.patient.age}, {SEX_LABEL[a.patient.sex]}</span></> : "—"}</td>
            <td>{a.practitionerName}</td>
            <td>{VISIT_TEXT[a.visitType]}</td>
            <td><span className={`appt-status st-${a.status}`}>{STATUS_TEXT[a.status]}</span>{a.cancelReason && <><br /><span className="muted small">{a.cancelReason}</span></>}</td>
            <td className="actions">
              {can("appointment:edit") && cancelling !== a.id && <>
                {(NEXT[a.status] ?? []).map((n) => <button key={n.to} className="btn btn-quiet sm" onClick={() => change(a, n.to)}>{n.label}</button>)}
                {can("queue:edit") && ["booked", "confirmed", "arrived"].includes(a.status) && a.date === istToday() && <button className="btn btn-quiet sm" onClick={() => checkIn(a)}>Check in</button>}
                {["booked", "confirmed"].includes(a.status) && <button className="btn btn-quiet sm" onClick={() => setRescheduling(a)}>Reschedule</button>}
                {["booked", "confirmed"].includes(a.status) && pastStart(a) && <button className="btn btn-quiet sm" onClick={() => change(a, "no_show")}>No-show</button>}
                {["booked", "confirmed", "arrived", "waiting"].includes(a.status) && <button className="btn btn-quiet sm danger" onClick={() => { setCancelling(a.id); setReason(""); }}>Cancel</button>}
              </>}
              {cancelling === a.id && <form className="inline-form" onSubmit={(e) => { e.preventDefault(); change(a, "cancelled", reason); }}>
                <Field label="Reason for cancelling" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                <Button type="submit" variant="secondary" disabled={reason.trim().length < 3}>Cancel appointment</Button>
                <button type="button" className="link-button" onClick={() => setCancelling(null)}>Keep it</button>
              </form>}
            </td>
          </tr>))}
        </tbody>
      </table>
      {rescheduling && <Reschedule a={rescheduling} onDone={(ok) => { setRescheduling(null); if (ok) onChanged(`${rescheduling.patient?.fullName}: moved to ${ok}.`); }} />}
    </>
  );
}

function SlotPicker({ doctorId, date, onPick, picked }: { doctorId: string; date: string; onPick: (s: Slot) => void; picked: string | null }) {
  const [slots, setSlots] = useState<Slot[] | null>(null);
  useEffect(() => { setSlots(null); if (doctorId && date) api.authed(`/api/v1/practitioners/${doctorId}/availability?from=${date}&to=${date}`).then((r) => setSlots(r.slots)).catch(() => setSlots([])); }, [doctorId, date]);
  if (!slots) return <p className="muted">Loading times…</p>;
  if (!slots.length) return <p className="empty">The doctor isn't consulting on this day.</p>;
  return (
    <div className="slots" role="radiogroup" aria-label="Available times">
      {slots.map((s) => (
        <button key={s.start} type="button" role="radio" aria-checked={picked === s.start} className={`slot${picked === s.start ? " on" : ""}${s.available === 0 ? " full" : ""}`}
          onClick={() => onPick(s)} title={s.available === 0 ? "Full: you can overbook with a reason" : `${s.available} place(s) left`}>
          {istTimeLabel(s.start)}{s.capacity > 1 && <small>{s.available} left</small>}{s.available === 0 && <small>Full</small>}
        </button>))}
    </div>
  );
}

function Reschedule({ a, onDone }: { a: Appt; onDone: (ok: string | null) => void }) {
  const [date, setDate] = useState(a.start ? new Date(new Date(a.start).getTime() + 330 * 60_000).toISOString().slice(0, 10) : istToday());
  const [slot, setSlot] = useState<Slot | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    if (!slot) return;
    try { await api.authed(`/api/v1/appointments/${a.id}/reschedule`, { body: { start: slot.start } }); onDone(`${dayLabel(date)}, ${istTimeLabel(slot.start)}`); }
    catch (ex) { setError((ex as ApiError).message); }
  }
  return (
    <div className="panel" role="dialog" aria-label={`Reschedule ${a.patient?.fullName}`}>
      <h2>Reschedule {a.patient?.fullName}</h2>
      {error && <Notice tone="error">{error}</Notice>}
      <Field label="New date" type="date" value={date} min={istToday()} hint={date ? dayLabel(date) : undefined} onChange={(e) => { setDate(e.target.value); setSlot(null); }} />
      <SlotPicker doctorId={a.practitionerId} date={date} picked={slot?.start ?? null} onPick={(s) => s.available > 0 && setSlot(s)} />
      <div className="form-actions"><Button onClick={go} disabled={!slot}>Move appointment</Button><Button variant="quiet" onClick={() => onDone(null)}>Close</Button></div>
    </div>
  );
}

/** Booking: patient, doctor, date, time; overbook with a reason; or add to the waiting list; or register a walk-in now. */
export function BookAppointment() {
  const { branchId } = useTenant();
  const { navigate } = useRouter();
  const q = new URLSearchParams(location.search);
  const doctors = useDoctors();
  const [patient, setPatient] = useState<{ id: string; fullName: string; uhid: string } | null>(null);
  const [search, setSearch] = useState(""), [results, setResults] = useState<{ id: string; fullName: string; uhid: string; age: string; sex: string; mobile: string | null }[] | null>(null);
  const [doctor, setDoctor] = useState(q.get("doctor") ?? "");
  const [date, setDate] = useState(istToday());
  const [slot, setSlot] = useState<Slot | null>(null);
  const [visitType, setVisitType] = useState("new");
  const [note, setNote] = useState(""), [overbook, setOverbook] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waitMsg, setWaitMsg] = useState<string | null>(null);

  useEffect(() => { const pid = q.get("patient"); if (pid) api.authed(`/api/v1/patients/${pid}`).then((r) => setPatient(r.patient)).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { if (!doctor && doctors.length === 1) setDoctor(doctors[0]!.id); }, [doctors, doctor]);

  async function find(e: FormEvent) { e.preventDefault(); if (search.trim().length < 2) return; setResults((await api.authed("/api/v1/patients/search", { body: { q: search } })).items); }
  async function book(walkIn = false) {
    if (!patient || !doctor) return;
    setBusy(true); setError(null);
    try {
      const r = await api.authed("/api/v1/appointments", { body: { patientId: patient.id, practitionerId: doctor, branchId, visitType: walkIn ? "walk_in" : visitType,
        source: "reception", start: walkIn ? null : slot?.start, note: note || null, overbookReason: slot && slot.available === 0 ? overbook : null } });
      if (walkIn) { await api.authed("/api/v1/queue/check-in", { body: { appointmentId: r.appointment.id } }); navigate(`/app/queue?doctor=${doctor}`); }
      else navigate(`/app/appointments?date=${r.appointment.date}&doctor=${doctor}`);
    } catch (ex) { setError((ex as ApiError).message); } finally { setBusy(false); }
  }
  async function waitlist() {
    try { await api.authed("/api/v1/waitlist", { body: { patientId: patient!.id, practitionerId: doctor, date } }); setWaitMsg(`${patient!.fullName} is on the waiting list for ${dayLabel(date)}. You'll see the name when a booking for that day is cancelled.`); }
    catch (ex) { setError((ex as ApiError).message); }
  }
  const full = slot && slot.available === 0;

  return (
    <NeedBranch>
      <h1>Book appointment</h1>
      {error && <Notice tone="error">{error}</Notice>}
      <section className="step-block" aria-labelledby="bk-p">
        <h2 id="bk-p">Patient</h2>
        {patient ? <p className="picked"><strong>{patient.fullName}</strong> <span className="mono muted">{patient.uhid}</span> <button className="link-button" onClick={() => setPatient(null)}>Change</button></p> : <>
          <form className="search-row" onSubmit={find}>
            <label className="sr-only" htmlFor="bk-search">Find patient by name, mobile or UHID</label>
            <input id="bk-search" placeholder="Name, mobile or UHID" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button type="submit" variant="secondary">Find patient</Button>
          </form>
          {results && (results.length ? <ul className="pick-list">{results.map((r) => <li key={r.id}><button onClick={() => setPatient(r)}>{r.fullName} <span className="mono muted">{r.uhid}</span> <span className="muted">{r.age}, {SEX_LABEL[r.sex]}{r.mobile ? `, ${r.mobile}` : ""}</span></button></li>)}</ul>
            : <p className="empty">No match. <Link to="/app/patients/new">Register the patient first</Link>.</p>)}
        </>}
      </section>
      <section className="step-block" aria-labelledby="bk-d">
        <h2 id="bk-d">Doctor and time</h2>
        <div className="row">
          <Select label="Doctor" placeholder="Choose a doctor" value={doctor} onChange={(e) => { setDoctor(e.target.value); setSlot(null); }} options={doctors.map((d) => ({ value: d.id, label: d.displayName }))} />
          {/* Browsers format date fields by their own locale (10/05 may mean 5 Oct or 10 May), so the day is spelled out below. */}
          <Field label="Date" type="date" min={istToday()} value={date} hint={date ? dayLabel(date) : undefined} onChange={(e) => { setDate(e.target.value); setSlot(null); setWaitMsg(null); }} />
        </div>
        {doctor && <SlotPicker doctorId={doctor} date={date} picked={slot?.start ?? null} onPick={setSlot} />}
        {full && <Field label="This time is full. Reason to add one more patient" hint="Saved in the audit log." value={overbook} onChange={(e) => setOverbook(e.target.value)} />}
        <div className="row">
          <Select label="Visit" value={visitType} onChange={(e) => setVisitType(e.target.value)} options={[{ value: "new", label: "New consultation" }, { value: "follow_up", label: "Follow-up" }]} />
          <Field label="Note for the doctor" optional value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </section>
      {waitMsg && <Notice tone="success">{waitMsg}</Notice>}
      <div className="form-actions">
        <Button busy={busy} disabled={!patient || !doctor || !slot || (!!full && overbook.trim().length < 5)} onClick={() => book()}>Book appointment</Button>
        <Button variant="secondary" disabled={!patient || !doctor} onClick={() => book(true)}>Walk-in: add to today's queue</Button>
        <Button variant="quiet" disabled={!patient || !doctor || date < istToday()} onClick={waitlist}>Add to waiting list</Button>
      </div>
    </NeedBranch>
  );
}

/** Upcoming appointments on the patient's page. */
export function PatientAppointments({ patientId }: { patientId: string }) {
  const { can } = useTenant();
  const [items, setItems] = useState<Appt[] | null>(null);
  useEffect(() => { api.authed(`/api/v1/patients/${patientId}/appointments`).then((r) => setItems(r.items)).catch(() => setItems([])); }, [patientId]);
  const now = Date.now();
  const upcoming = (items ?? []).filter((a) => new Date(a.start).getTime() >= now - 3_600_000 && ["booked", "confirmed", "arrived", "waiting", "in_consultation"].includes(a.status));
  const past = (items ?? []).filter((a) => !upcoming.includes(a)).slice(-5).reverse();
  return (
    <section aria-labelledby="pa-h">
      <div className="page-head"><h2 id="pa-h">Appointments</h2>{can("appointment:create") && <Link to={`/app/appointments/new?patient=${patientId}`} className="btn btn-secondary">Book appointment</Link>}</div>
      {items && !items.length && <p className="empty">No appointments yet.</p>}
      {upcoming.length > 0 && <ul className="plain">{upcoming.map((a) => <li key={a.id}><strong>{a.dateLabel}, {a.timeLabel}</strong> with {a.practitionerName} <span className={`appt-status st-${a.status}`}>{STATUS_TEXT[a.status]}</span></li>)}</ul>}
      {past.length > 0 && <details><summary>Earlier</summary><ul className="plain">{past.map((a) => <li key={a.id}>{a.dateLabel}, {a.timeLabel}, {a.practitionerName}: {STATUS_TEXT[a.status]}</li>)}</ul></details>}
    </section>
  );
}
